// src/store/authStore.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import api from '../lib/api'; // Removed alertsApi if not used here, adjust if needed
import type { UserProfile, UserSubscription } from '../types/api';
import { mixpanelService } from '../lib/mixpanelService';
import logger from '../lib/logger';

// Keep JWTPayload simple for decoding basic info
interface JWTPayload {
  exp: number;
  user_id: string; // Expect at least user_id
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoadingUser: boolean; // Add loading state for user fetch
  error: string | null; // Add the error property
  subscription: UserSubscription | null;
  login: (tokens: { access: string; refresh: string }) => Promise<void>; // Make async
  logout: () => void;
  setTokens: (tokens: { access: string; refresh: string }) => Promise<void>; // Make async
  fetchAndSetUser: () => Promise<void>; // New action
  updateSubscription: (subscription: UserSubscription | null) => void;
  // debug: () => void; // Keep debug if needed
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoadingUser: false, // Initialize loading state
      error: null, // Initialize the error state
      subscription: null, // Initialize subscription state

      // Simplified login: just store tokens and flag as authenticated
      login: async (tokens) => {
        logger.log('AuthStore: login called with tokens');
        try {
          // Minimal validation: check if tokens exist
          if (!tokens.access || !tokens.refresh) {
            throw new Error('Access or Refresh token missing');
          }
          set({
            accessToken: tokens.access,
            refreshToken: tokens.refresh,
            isAuthenticated: true, // Set authenticated flag immediately
            user: null, // Clear any old user data initially
            isLoadingUser: true, // Start loading user data
            error: null, // Clear previous errors (This will now work)
          });
          logger.log('AuthStore: Tokens set, isAuthenticated=true');
          // Trigger fetching user details *after* setting tokens
          await get().fetchAndSetUser();
        } catch (error) {
          console.error('AuthStore: Error during token storage:', error);
          get().logout(); // Ensure logout on error
        }
      },

      // New action to fetch user details
      fetchAndSetUser: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          logger.log('AuthStore: No access token, cannot fetch user.');
          set({ isLoadingUser: false }); // Stop loading if no token
          return;
        }
        // Decode just to ensure validity/get user_id if needed for endpoint,
        // although usually backend identifies user from token itself.
        let userId: string | null = null;
        try {
          const decoded = jwtDecode<JWTPayload>(accessToken);
          userId = decoded.user_id; // Assuming your /users/me/ endpoint doesn't need ID explicitly
          logger.log('AuthStore: Decoded user_id:', userId);
        } catch (e) {
          console.error('AuthStore: Cannot decode token to fetch user, logging out.', e);
          get().logout();
          return;
        }

        set({ isLoadingUser: true, error: null }); // Indicate loading user (This will now work)
        try {
          // Make API call to a '/users/me/' endpoint (adjust if needed)
          // This endpoint should be protected and return the logged-in user's details
          const response = await api.get<UserProfile>('/users/me/'); // *** YOU NEED TO CREATE THIS BACKEND ENDPOINT ***
          logger.log('AuthStore: Fetched user data:', response.data);
          set({
            user: response.data,
            subscription: response.data.subscription || null,
            isLoadingUser: false,
          });

          // Track successful login
          mixpanelService.trackLogin(response.data, 'jwt');
        } catch (error) {
          console.error('AuthStore: Failed to fetch user data:', error);
          // Ensure the error message is a string or null
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to load user profile.';
          set({
            error: errorMessage,
            isLoadingUser: false,
            user: null /* Optionally clear user */,
          }); // This will now work
        }
      },

      logout: () => {
        logger.log('AuthStore: logout called');

        // Track logout before clearing user data
        mixpanelService.trackLogout();

        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          isLoadingUser: false,
          error: null, // Clear error on logout
          subscription: null, // Clear subscription on logout
        });
        // Optionally: Call backend blacklist endpoint here if implemented
      },

      // setTokens might be called by refresh interceptor - needs to fetch user too
      setTokens: async (tokens) => {
        logger.log('AuthStore: setTokens called');
        try {
          if (!tokens.access || !tokens.refresh) throw new Error('Access or Refresh token missing');
          set({
            accessToken: tokens.access,
            refreshToken: tokens.refresh,
            isAuthenticated: true,
            user: null,
            isLoadingUser: true,
            error: null,
          });
          logger.log('AuthStore: Tokens set via setTokens, isAuthenticated=true');
          await get().fetchAndSetUser(); // Fetch user after setting new tokens
        } catch (error) {
          console.error('AuthStore: Error during setTokens:', error);
          get().logout();
        }
      },

      updateSubscription: (subscription) => {
        set({ subscription });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist tokens and basic auth status
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // Don't persist user details directly, fetch on load if needed
      }),
      // Rehydrate: Fetch user data when loading persisted state
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('AuthStore: Failed to rehydrate state', error);
          } else if (state?.isAuthenticated && state?.accessToken) {
            logger.log('AuthStore: Rehydrated, fetching user data...');
            // Use setTimeout to ensure hydration completes before fetch
            setTimeout(() => state.fetchAndSetUser(), 0);
          } else {
            logger.log('AuthStore: Rehydrated, not authenticated.');
            // Ensure loading state is false if not authenticated on rehydrate
            if (state) state.isLoadingUser = false;
          }
        };
      },
    }
  )
);

// Initial check after store creation
logger.log('Initial auth store state:', useAuthStore.getState());
